import { useEffect, useState } from "react";
import api from "../../lib/api";
import { DollarSign, Repeat, XCircle, MailCheck, MailX, Plus, Send, Trash2, X, Save, Smartphone, Landmark, Wallet } from "lucide-react";

const CURRENCY_SYM = { usd: "$", eur: "€", gbp: "£", ssp: "SSP " };

const METHOD_META = {
    mobile_money: { label: "Mobile Money", icon: Smartphone },
    bank: { label: "Bank Transfer", icon: Landmark },
    cash: { label: "Cash", icon: Wallet },
    other: { label: "Other", icon: Wallet },
};

export default function DonationsAdmin() {
    const [items, setItems] = useState([]);
    const [showOffline, setShowOffline] = useState(false);
    const load = () => api.get("/donations").then(({ data }) => setItems(data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const paid = items.filter((d) => d.payment_status === "paid");
    const oneTimeTotal = paid.filter((d) => d.interval !== "month").reduce((s, d) => s + (d.amount || 0), 0);
    const offlineTotal = paid.filter((d) => d.offline).reduce((s, d) => s + (d.amount || 0), 0);
    const activeSubs = items.filter((d) => d.interval === "month" && d.subscription_status === "active");
    const monthlyRecurring = activeSubs.reduce((s, d) => s + (d.amount || 0), 0);

    const cancel = async (sessionId) => {
        if (!window.confirm("Cancel this monthly subscription? The donor will stop being billed immediately.")) return;
        try { await api.post(`/donations/${sessionId}/cancel-subscription`); load(); }
        catch (e) { alert(e?.response?.data?.detail || "Cancel failed"); }
    };
    const resend = async (sessionId) => {
        try { await api.post(`/donations/${sessionId}/resend-receipt`); load(); alert("Receipt resent (if email key configured)."); }
        catch (e) { alert(e?.response?.data?.detail || "Resend failed"); }
    };
    const del = async (sessionId) => {
        if (!window.confirm("Delete this offline donation record?")) return;
        try { await api.delete(`/donations/${sessionId}`); load(); }
        catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div data-testid="donations-admin">
            <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-4xl mb-2">Donations</h1>
                    <p className="text-muted-foreground">Stripe transactions + offline (mobile money / bank / cash) records. Donors automatically receive an email receipt when an email is on file.</p>
                </div>
                <button onClick={() => setShowOffline(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-secondary text-secondary-foreground hover:bg-secondary/90 text-sm font-medium" data-testid="record-offline-button">
                    <Plus size={14} /> Record offline donation
                </button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
                <Stat label="One-time raised" value={`$${oneTimeTotal.toFixed(2)}`} icon={DollarSign} />
                <Stat label="Offline raised" value={`$${offlineTotal.toFixed(2)}`} icon={Wallet} />
                <Stat label="Monthly recurring" value={`$${monthlyRecurring.toFixed(2)}`} icon={Repeat} hint={`${activeSubs.length} active`} />
                <Stat label="Total transactions" value={items.length} icon={DollarSign} />
            </div>

            <div className="bg-card border border-border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        <tr>
                            <th className="text-left p-4">Date</th>
                            <th className="text-left p-4">Donor</th>
                            <th className="text-left p-4">Source</th>
                            <th className="text-right p-4">Amount</th>
                            <th className="text-left p-4">Status</th>
                            <th className="text-center p-4">Receipt</th>
                            <th className="text-right p-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((d) => {
                            const isSub = d.interval === "month";
                            const subActive = isSub && d.subscription_status === "active";
                            const Meta = METHOD_META[d.method] || (d.offline ? METHOD_META.other : null);
                            return (
                                <tr key={d.session_id} className="border-t border-border" data-testid={`donation-${d.session_id}`}>
                                    <td className="p-4 text-muted-foreground">{new Date(d.created_at).toLocaleString()}</td>
                                    <td className="p-4">
                                        <div className="font-medium">{d.donor_name || "Anonymous"}</div>
                                        {d.donor_email && <div className="text-xs text-muted-foreground">{d.donor_email}</div>}
                                    </td>
                                    <td className="p-4 text-muted-foreground">
                                        {d.offline ? (
                                            <span className="inline-flex items-center gap-1.5">
                                                {Meta?.icon && <Meta.icon size={12} className="text-secondary" />}
                                                {Meta?.label || "Offline"}
                                            </span>
                                        ) : (
                                            <span>
                                                {d.label}
                                                {isSub && <span className="ml-2 inline-flex items-center gap-1 text-xs text-secondary"><Repeat size={10} /> monthly</span>}
                                            </span>
                                        )}
                                        {d.reference && <div className="text-[10px] font-mono text-muted-foreground mt-1">Ref: {d.reference}</div>}
                                    </td>
                                    <td className="p-4 text-right font-mono">
                                        {(CURRENCY_SYM[(d.currency || "usd").toLowerCase()] || `${(d.currency || "").toUpperCase()} `)}{(d.amount || 0).toFixed(2)}
                                        {isSub && <span className="text-xs text-muted-foreground">/mo</span>}
                                    </td>
                                    <td className="p-4">
                                        <span className={`inline-block px-2 py-1 text-xs rounded-sm uppercase tracking-[0.18em] ${
                                            d.payment_status === "paid" ? "bg-secondary/20 text-secondary"
                                            : d.payment_status === "unpaid" ? "bg-muted text-muted-foreground"
                                            : "bg-accent text-accent-foreground"
                                        }`}>{d.payment_status || "—"}</span>
                                        {isSub && d.subscription_status && (
                                            <div className="text-[10px] uppercase tracking-[0.18em] mt-1 text-muted-foreground">sub: {d.subscription_status}</div>
                                        )}
                                    </td>
                                    <td className="p-4 text-center">
                                        {d.donor_email ? (
                                            d.receipt_sent ? <MailCheck size={16} className="text-secondary inline" /> : <MailX size={16} className="text-muted-foreground inline" />
                                        ) : <span className="text-xs text-muted-foreground">—</span>}
                                    </td>
                                    <td className="p-4 text-right space-x-2 whitespace-nowrap">
                                        {d.donor_email && d.payment_status === "paid" && (
                                            <button onClick={() => resend(d.session_id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-border hover:border-primary/60 text-xs" data-testid={`resend-${d.session_id}`}>
                                                <Send size={11} /> Resend
                                            </button>
                                        )}
                                        {subActive && (
                                            <button onClick={() => cancel(d.session_id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-destructive text-destructive hover:bg-destructive/10 text-xs" data-testid={`cancel-${d.session_id}`}>
                                                <XCircle size={11} /> Cancel
                                            </button>
                                        )}
                                        {d.offline && (
                                            <button onClick={() => del(d.session_id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm border border-destructive text-destructive hover:bg-destructive/10 text-xs" data-testid={`delete-${d.session_id}`}>
                                                <Trash2 size={11} /> Delete
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {items.length === 0 && (
                            <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">No donations yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {showOffline && <OfflineModal onClose={() => setShowOffline(false)} onCreated={() => { setShowOffline(false); load(); }} />}
        </div>
    );
}

const OfflineModal = ({ onClose, onCreated }) => {
    const [form, setForm] = useState({
        donor_name: "", donor_email: "", amount: "", currency: "usd",
        method: "mobile_money", reference: "", note: "", send_receipt: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const set = (k, v) => setForm({ ...form, [k]: v });

    const submit = async (e) => {
        e.preventDefault(); setError(""); setSubmitting(true);
        try {
            const payload = { ...form, amount: parseFloat(form.amount), donor_email: form.donor_email || undefined };
            if (!payload.donor_name) { setError("Donor name is required"); setSubmitting(false); return; }
            if (!payload.amount || payload.amount <= 0) { setError("Amount must be > 0"); setSubmitting(false); return; }
            await api.post("/donations/offline", payload);
            onCreated();
        } catch (err) {
            setError(err?.response?.data?.detail || "Could not record donation");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-start justify-center p-6 overflow-y-auto" data-testid="offline-modal">
            <form onSubmit={submit} className="bg-card border border-border rounded-sm w-full max-w-xl my-10">
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <div>
                        <h2 className="font-display text-2xl">Record offline donation</h2>
                        <p className="text-xs text-muted-foreground mt-1">For mobile money, bank transfers, cash, and in-kind gifts.</p>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 rounded-sm hover:bg-muted"><X size={18} /></button>
                </div>
                <div className="p-6 space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <F label="Donor name *" value={form.donor_name} onChange={(v) => set("donor_name", v)} testid="off-name" />
                        <F label="Donor email (for receipt)" type="email" value={form.donor_email} onChange={(v) => set("donor_email", v)} testid="off-email" />
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <F label="Amount *" type="number" value={form.amount} onChange={(v) => set("amount", v)} testid="off-amount" />
                        <label className="block">
                            <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Currency</span>
                            <select value={form.currency} onChange={(e) => set("currency", e.target.value)} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="off-currency">
                                <option value="usd">USD</option>
                                <option value="eur">EUR</option>
                                <option value="gbp">GBP</option>
                                <option value="ssp">SSP</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Method</span>
                            <select value={form.method} onChange={(e) => set("method", e.target.value)} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" data-testid="off-method">
                                <option value="mobile_money">Mobile Money</option>
                                <option value="bank">Bank Transfer</option>
                                <option value="cash">Cash</option>
                                <option value="other">Other</option>
                            </select>
                        </label>
                    </div>
                    <F label="Reference / transaction ID" value={form.reference} onChange={(v) => set("reference", v)} testid="off-reference" />
                    <T label="Internal note" value={form.note} onChange={(v) => set("note", v)} testid="off-note" />
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.send_receipt} onChange={(e) => set("send_receipt", e.target.checked)} data-testid="off-send-receipt" />
                        <span className="text-sm">Send email receipt to donor (if email provided)</span>
                    </label>
                    {error && <div className="text-sm text-destructive" data-testid="off-error">{error}</div>}
                </div>
                <div className="p-6 border-t border-border flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-sm border border-border text-sm">Cancel</button>
                    <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60" data-testid="off-submit">
                        <Save size={14} /> {submitting ? "Saving…" : "Record donation"}
                    </button>
                </div>
            </form>
        </div>
    );
};

const F = ({ label, value, onChange, testid, type = "text" }) => (
    <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm" /></label>
);
const T = ({ label, value, onChange, testid }) => (
    <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">{label}</span>
        <textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2.5 rounded-sm border border-border bg-background text-sm resize-y" /></label>
);

const Stat = ({ label, value, icon: Icon, hint }) => (
    <div className="bg-card border border-border rounded-sm p-6">
        <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2 inline-flex items-center gap-2"><Icon size={12} /> {label}</div>
        <div className="font-display text-3xl">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
);
